import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

type GalleryItem = {
  id: string;
  category: string;
  before_image_url: string | null;
  after_image_url: string | null;
  merged_image_url: string | null;
  treatment: string;
  description: string | null;
  is_published: boolean;
  created_at: string;
};

const categories = ["Implants", "Smile Makeover", "Aligners", "Whitening", "Crowns", "Braces", "Root Canal", "General"];
const emptyForm = { category: "General", merged_image_url: "", treatment: "", description: "", is_published: false };

const AdminGallery = () => {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchItems = async () => {
    const { data } = await supabase.from("gallery_items").select("*").order("created_at", { ascending: false });
    if (data) setItems(data);
  };

  useEffect(() => { fetchItems(); }, []);

  const uploadImage = async (file: File, field: "merged_image_url") => {
    setUploading(true);
    const ext = file.name.split(".").pop();
    const path = `gallery/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from("admin-uploads").upload(path, file);
    if (error) { toast.error("Upload failed"); setUploading(false); return; }
    const { data: { publicUrl } } = supabase.storage.from("admin-uploads").getPublicUrl(path);
    setForm({ ...form, [field]: publicUrl });
    setUploading(false);
  };

  const handleSave = async () => {
    const payload = {
      category: form.category,
      merged_image_url: form.merged_image_url,
      treatment: form.treatment,
      description: form.description || null,
      is_published: form.is_published,
    };

    if (editId) {
      await supabase.from("gallery_items").update(payload).eq("id", editId);
      toast.success("Gallery item updated");
    } else {
      await supabase.from("gallery_items").insert(payload);
      toast.success("Gallery item created");
    }
    setOpen(false);
    setForm(emptyForm);
    setEditId(null);
    fetchItems();
  };

  const handleEdit = (item: GalleryItem) => {
    setForm({
      category: item.category,
      merged_image_url: item.merged_image_url || "",
      treatment: item.treatment,
      description: item.description || "",
      is_published: item.is_published,
    });
    setEditId(item.id);
    setOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this gallery item?")) return;
    await supabase.from("gallery_items").delete().eq("id", id);
    toast.success("Gallery item deleted");
    fetchItems();
  };

  return (
    <div className="space-y-6 lg:space-y-8">
      {/* Hero */}
      <section className="admin-gradient-hero rounded-3xl px-6 lg:px-10 py-8 lg:py-10">
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-semibold">Smile Gallery</h1>
            <p className="text-white/70 mt-2 text-sm lg:text-base">Managing {items.length} curated patient transformations.</p>
          </div>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(emptyForm); setEditId(null); } }}>
            <DialogTrigger asChild>
              <button className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-[hsl(var(--admin-navy))] text-sm font-semibold hover:bg-white/90 transition shadow-lg">
                <Plus className="h-4 w-4" /> New Item
              </button>
            </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editId ? "Edit Gallery Item" : "Add Gallery Item"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Treatment name" value={form.treatment} onChange={(e) => setForm({ ...form, treatment: e.target.value })} />
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Textarea placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />

              <div>
                <label className="text-xs font-medium mb-1 block">Merged Before/After Image</label>
                <p className="text-[11px] text-muted-foreground mb-2">
                  Upload a single image with Before on the left and After on the right (Arizzta-style merged frame).
                </p>
                {form.merged_image_url && <img src={form.merged_image_url} alt="Merged before/after" className="h-32 w-full rounded mb-1 object-cover" />}
                <Input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0], "merged_image_url")} disabled={uploading} />
              </div>

              <div className="flex items-center gap-2">
                <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} />
                <span className="text-sm">Published</span>
              </div>

              <Button onClick={handleSave} className="w-full" disabled={!form.treatment || !form.merged_image_url}>
                {editId ? "Update" : "Create"} Item
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </section>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((item) => (
          <div key={item.id} className="admin-card overflow-hidden group">
            <div className="relative">
              {item.merged_image_url ? (
                <img src={item.merged_image_url} alt={item.treatment} className="w-full h-44 object-cover" />
              ) : (
                <div className="grid grid-cols-2 gap-0.5 h-44">
                  {item.before_image_url && <img src={item.before_image_url} alt="Before" className="w-full h-full object-cover" />}
                  {item.after_image_url && <img src={item.after_image_url} alt="After" className="w-full h-full object-cover" />}
                </div>
              )}
              <span className={`absolute top-3 right-3 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full ${item.is_published ? "bg-emerald-500 text-white" : "bg-slate-700/80 text-white"}`}>
                {item.is_published ? "Live" : "Draft"}
              </span>
              <div className="absolute bottom-3 left-3 px-2.5 py-1 rounded-md bg-black/55 backdrop-blur text-white text-xs font-medium flex items-center gap-1.5">
                📍 {item.category}
              </div>
            </div>
            <div className="p-4">
              <p className="font-semibold text-sm truncate">{item.treatment}</p>
              <div className="flex items-center justify-between mt-3">
                <Switch checked={item.is_published} onCheckedChange={async () => {
                  await supabase.from("gallery_items").update({ is_published: !item.is_published }).eq("id", item.id);
                  fetchItems();
                }} />
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" aria-label="Edit gallery item" onClick={() => handleEdit(item)}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" aria-label="Delete gallery item" onClick={() => handleDelete(item.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-[hsl(var(--admin-muted))] text-center py-12 col-span-full admin-card">No gallery items yet.</p>}
      </div>
    </div>
  );
};

export default AdminGallery;
